import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import AIFinanceCanvas from "./AIFinanceCanvas";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.25 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

// Fallback static data while live data loads
const FALLBACK = {
  nse:    { price: "24,612", pct: 0.42, direction: "up" },
  sensex: { price: "81,420", pct: 0.31, direction: "up" },
  usdinr: { price: "84.21",  pct: 0.06, direction: "down" },
  brent:  { price: "78.92",  pct: 1.10, direction: "up" },
  gold:   { price: "₹89,200", pct: 0.25, direction: "up" },
};

function TickerItem({ label, price, pct, direction }) {
  const isUp = direction === "up";
  return (
    <span className="flex items-center gap-1">
      <span className="text-slate-400">{label} · </span>
      <span className="text-slate-200">{price}</span>
      <span className={isUp ? "text-emerald-400" : "text-rose-400"}>
        {isUp ? "▲" : "▼"} {Number(pct).toFixed(2)}%
      </span>
    </span>
  );
}

export default function Hero() {
  const [market, setMarket] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchMarket = () => {
      fetch("/api/market.php")
        .then((r) => r.json())
        .then((data) => {
          // Merge with fallback for any null values
          const merged = {};
          Object.keys(FALLBACK).forEach((k) => {
            merged[k] = data[k] || FALLBACK[k];
          });
          setMarket(merged);
          setLoaded(true);
        })
        .catch(() => { setLoaded(true); }); // keep fallback on error
    };

    fetchMarket();
    const interval = setInterval(fetchMarket, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="home"
      className="relative h-screen w-full overflow-hidden bg-[#0b1220] text-white"
      data-testid="hero-section"
    >
      {/* Animated AI + Finance background */}
      <AIFinanceCanvas />

      {/* Subtle gradient to ground type at center bottom */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-[#0b1220]/30 to-[#070b14]" />

      {/* Top corner meta */}
      <div className="absolute top-24 left-6 md:left-10 z-10 hidden md:flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase text-emerald-300/80 font-mono-x">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.9)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 pt-24">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="text-center max-w-5xl"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-sky-200/85 tracking-[0.28em] uppercase mb-5 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" />
              AI · Financial Intelligence Suite
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-display font-normal text-white/35 text-7xl md:text-8xl lg:text-[9rem] leading-[0.9] tracking-[-0.04em]"
              data-testid="hero-headline-line1"
            >
              Precision.
            </motion.h1>
            <motion.h1
              variants={fadeUp}
              className="hero-line-2 font-display font-semibold text-7xl md:text-8xl lg:text-[9rem] leading-[0.9] tracking-[-0.05em] text-white"
              data-testid="hero-headline-line2"
            >
              <span className="bg-gradient-to-b from-white via-white to-sky-200 bg-clip-text text-transparent">
                Intelligence.
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-7 text-lg md:text-xl text-slate-300/90 max-w-2xl mx-auto leading-relaxed"
              data-testid="hero-subtitle"
            >
              Audit-grade financial tools — engineered by a Chartered
              Accountant, accelerated by AI. Lease accounting, ESOP valuation,
              consolidation &amp; audit, in one console.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-9 flex items-center justify-center gap-3 md:gap-4 flex-wrap"
            >
              <a
                href="#tools"
                className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 backdrop-blur border border-white/15 text-white font-medium hover:bg-white/10 transition-colors"
                data-testid="hero-cta-discover"
              >
                Discover
                <ArrowDown
                  size={16}
                  className="transition-transform group-hover:translate-y-0.5"
                />
              </a>
              <a
                href="/hub/"
                className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0b1220] font-semibold hover:bg-sky-50 transition-colors shadow-[0_10px_40px_-10px_rgba(186,230,253,0.6)]"
                data-testid="hero-cta-book"
              >
                Open Workspace
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="mt-10 flex items-center justify-center gap-6 text-[11px] uppercase tracking-[0.2em] text-slate-400 font-mono-x"
            >
              <span>3 tools</span>
              <span className="h-3 w-px bg-slate-600" />
              <span>Ind AS 116</span>
              <span className="h-3 w-px bg-slate-600" />
              <span>IFRS 16</span>
              <span className="h-3 w-px bg-slate-600" />
              <span>Ind AS 102</span>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom ticker bar — live market data */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="relative pb-6 md:pb-8 px-6 md:px-10"
          data-testid="hero-ticker"
        >
          <div className="h-px w-full mb-4 bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
          <div className="flex items-center justify-between text-[11px] md:text-xs font-mono-x uppercase tracking-[0.18em]">
            <TickerItem label="NSE" price={market.nse?.price} pct={market.nse?.pct} direction={market.nse?.direction} />
            <span className="hidden md:flex">
              <TickerItem label="SENSEX" price={market.sensex?.price} pct={market.sensex?.pct} direction={market.sensex?.direction} />
            </span>
            <span className="hidden md:flex">
              <TickerItem label="USD/INR" price={market.usdinr?.price} pct={market.usdinr?.pct} direction={market.usdinr?.direction} />
            </span>
            <span className="hidden lg:flex">
              <TickerItem label="Brent" price={market.brent?.price} pct={market.brent?.pct} direction={market.brent?.direction} />
            </span>
            <span className="hidden lg:flex">
              <TickerItem label="Gold" price={market.gold?.price} pct={market.gold?.pct} direction={market.gold?.direction} />
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
